import style from './index.module.css'

import React, { useState } from 'react'

import {
  getNativeTags,
  getPlaylistTags,
} from '@disco/state-management/selectors/tags'
import { normalizeTagsFilterValue } from '@disco/utils/search-page'

import { FilterComponentProps, TagsFilterValue } from 'types/filter-page'
import type { PlaylistTag } from 'types/playlist'
import type { NativeTag, NativeTagWithCategory } from 'types/track'

import { Flex, Separator, Text } from 'components/ds'
import Accordion from 'components/ui/accordion'

import { useSelector } from 'hooks/use-redux'

import TagSearchTextField from './filter-search'
import SelectedTags from './filter-search/selected-tags'
import TagsCheckbox from './tag-checkbox'
import {
  getCategoriesAndTags,
  getFilteredSelectedTags,
  getFilteredUnselectedTags,
  getMatchingTagCategories,
} from './utils'

// Playlist tags have no native category, so they get their own group beside
// the category ones. Doubles as the accordion's identity/open-state key.
const PLAYLIST_GROUP = 'Playlist'

const TagsForm = ({
  value,
  onChange,
  operator = 'equals',
}: FilterComponentProps) => {
  const nativeTags: NativeTagWithCategory[] = useSelector(getNativeTags)
  const playlistTags: PlaylistTag[] = useSelector(getPlaylistTags)

  const { tags: selectedIds, playlistTags: selectedNames } =
    normalizeTagsFilterValue(value)

  const [searchValue, setSearchValue] = useState<string>('')
  const [isAccordionOpen, setIsAccordionOpen] = useState<string[]>([])
  const [visibleTags, setVisibleTags] =
    useState<NativeTagWithCategory[]>(nativeTags)

  // An empty filter matches everything, so it clears instead of committing.
  const commit = (next: TagsFilterValue) =>
    onChange(
      next.tags.length || next.playlistTags.length ? next : undefined,
      operator,
    )

  const handleSearchFilters = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value
    setSearchValue(rawValue)

    const searchInput = rawValue.toLowerCase().trim()
    if (!searchInput) {
      setIsAccordionOpen([])
      setVisibleTags(nativeTags)
      return
    }

    const filteredTags = nativeTags.filter(tag =>
      tag.name.toLowerCase().includes(searchInput),
    )
    setVisibleTags(filteredTags)

    const matchingGroups = getMatchingTagCategories(searchInput, filteredTags)
    const hasPlaylistMatch = playlistTags.some(tag =>
      tag.name.toLowerCase().includes(searchInput),
    )
    setIsAccordionOpen(
      hasPlaylistMatch ? [...matchingGroups, PLAYLIST_GROUP] : matchingGroups,
    )
  }

  const handleTagCheck = (checked: string | boolean, tag: NativeTag) =>
    commit({
      tags: checked
        ? [...selectedIds, tag.id]
        : selectedIds.filter(id => id !== tag.id),
      playlistTags: selectedNames,
    })

  const handlePlaylistTagCheck = (
    checked: string | boolean,
    tag: PlaylistTag,
  ) =>
    commit({
      tags: selectedIds,
      playlistTags: checked
        ? [...selectedNames, tag.name]
        : selectedNames.filter(name => name !== tag.name),
    })

  const selectedNativeTags = getFilteredSelectedTags(
    selectedIds,
    searchValue,
    nativeTags,
    tag => tag.id,
  )
  const selectedPlaylistTags = getFilteredSelectedTags(
    selectedNames,
    searchValue,
    playlistTags,
    tag => tag.name,
  )

  const tagGroups = getCategoriesAndTags(visibleTags, selectedIds)
  const unselectedPlaylistTags = getFilteredUnselectedTags(
    selectedNames,
    searchValue,
    playlistTags,
    tag => tag.name,
  )

  const accordionItems = [
    ...Object.entries(tagGroups).map(([categoryName, tags]) => ({
      id: categoryName,
      title: categoryName,
      content: (
        <TagsCheckbox
          tagOptions={tags}
          onCheckChange={handleTagCheck}
          value={selectedIds}
          searchValue={searchValue}
        />
      ),
    })),
    ...(unselectedPlaylistTags.length
      ? [
          {
            id: PLAYLIST_GROUP,
            title: PLAYLIST_GROUP,
            content: (
              <TagsCheckbox
                tagOptions={unselectedPlaylistTags}
                // Duplicate-named playlist tags mean the same thing to the
                // backend, so they share selection but keep their own rows.
                getKey={tag => tag.name}
                onCheckChange={handlePlaylistTagCheck}
                value={selectedNames}
                searchValue={searchValue}
              />
            ),
          },
        ]
      : []),
  ]
    // Category order otherwise follows whichever one owns the alphabetically
    // first tag. The playlist group sorts in as a peer of the categories.
    .sort((a, b) =>
      a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }),
    )

  const isSeparatorVisible =
    selectedNativeTags.length + selectedPlaylistTags.length > 0 &&
    accordionItems.length > 0

  return (
    <Flex direction="column" width="100%" position="relative">
      <TagSearchTextField
        searchValue={searchValue}
        onSearch={handleSearchFilters}
      />
      <Flex direction="column" width="100%">
        <SelectedTags
          selectedTags={selectedNativeTags}
          onRemoveTag={tag => handleTagCheck(false, tag)}
          searchValue={searchValue}
        />
        <SelectedTags
          selectedTags={selectedPlaylistTags}
          onRemoveTag={tag => handlePlaylistTagCheck(false, tag)}
          searchValue={searchValue}
        />
        {isSeparatorVisible && <Separator size="4" my="2" />}
        <Flex direction="column">
          {accordionItems.map(item => (
            <Accordion
              key={item.id}
              title={<Text size="2">{item.title}</Text>}
              arrowType="arrow-down"
              scrollIntoView={false}
              titleClassName={style.Tag_AccordionTitle}
              contentClassName={style.Tag_AccordionContent}
              adjustedContentHeight="min-content"
              expanded={isAccordionOpen.includes(item.title)}
              onTitleClick={() => setIsAccordionOpen([item.title])}
            >
              {item.content}
            </Accordion>
          ))}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default TagsForm
